import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, Check, Circle, Download, List, RefreshCw, Wand2 } from "lucide-react";

import { useStore } from "../context/StoreContext";
import CalendarGrid from "../components/CalendarGrid";
import PostCard from "../components/posts/PostCard";
import PostEditor from "../components/posts/PostEditor";
import { FilterChip, PageHeader } from "../components/bits";
import { EmptyHint, GhostBtn, PrimaryBtn, Segmented } from "../components/ui";
import { fmtDateLong, todayISO } from "../lib/format";
import { PLATFORMS } from "../lib/platforms";

function ExportMenu({ onExport }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const pick = (fmt) => {
    setOpen(false);
    onExport(fmt);
  };
  return (
    <div ref={ref} className="relative">
      <GhostBtn full onClick={() => setOpen((o) => !o)}>
        <Download size={15} />
        Download
      </GhostBtn>
      {open ? (
        <div className="absolute right-0 mt-1.5 w-44 bg-popover border border-border rounded-xl shadow-xl z-30 overflow-hidden py-1">
          <button onClick={() => pick("pdf")} className="w-full text-left px-3.5 py-2 text-sm text-foreground hover:bg-foreground/5 transition">
            PDF
          </button>
          <button onClick={() => pick("docx")} className="w-full text-left px-3.5 py-2 text-sm text-foreground hover:bg-foreground/5 transition">
            Word (.docx)
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function CalendarPage() {
  const {
    posts,
    connectedPlatforms,
    documents,
    kbDirty,
    openGenerate,
    deletePost,
    regeneratePost,
    copyPost,
    updatePost,
    regenId,
    exportCalendar,
    selectedDay,
    setSelectedDay,
  } = useStore();
  const navigate = useNavigate();

  const [mode, setMode] = useState("calendar");
  const [filter, setFilter] = useState("all");
  const selected = selectedDay;
  const setSelected = setSelectedDay;
  const [editPost, setEditPost] = useState(null);

  const hasDocs = documents.length > 0;
  const connected = connectedPlatforms;

  // Default the selected day to today, so the calendar opens on the current month.
  // A stored selection (e.g. after visiting a storyboard or UGC page) is kept.
  useEffect(() => {
    if (selected) return;
    setSelected(todayISO());
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = filter === "all" ? posts : posts.filter((p) => p.platform === filter);

  const byDate = useMemo(() => {
    const m = {};
    visible.forEach((p) => {
      (m[p.date] ||= []).push(p);
    });
    return Object.keys(m)
      .sort()
      .map((d) => ({ date: d, items: m[d].sort((a, b) => a.time.localeCompare(b.time)) }));
  }, [visible]);

  const dayPosts = useMemo(
    () => posts.filter((p) => p.date === selected).sort((a, b) => a.time.localeCompare(b.time)),
    [posts, selected],
  );

  const VIDEO = new Set(["tiktok", "youtube", "instagram"]);
  const cardHandlers = (p) => ({
    onEdit: () => setEditPost(p),
    onDelete: () => deletePost(p.id),
    onRegenerate: () => regeneratePost(p),
    onCopy: () => copyPost(p),
    onStoryboard: VIDEO.has(p.platform) ? () => navigate(`/storyboard/${p.id}`) : null,
    onUgc: () => navigate(`/ugc/${p.id}`),
    regenerating: regenId === p.id,
  });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Content calendar"
        subtitle="Posts generated from your knowledge base, scheduled across the period."
        right={
          posts.length > 0 ? (
            <>
              <ExportMenu onExport={exportCalendar} />
              <PrimaryBtn onClick={openGenerate}>
                <RefreshCw size={16} />
                Regenerate
              </PrimaryBtn>
            </>
          ) : null
        }
      />

      {kbDirty && posts.length > 0 ? (
        <div className="border border-border rounded-2xl p-3.5 mb-5 flex items-center gap-3 bg-foreground/[0.04] animate-fade-in">
          <RefreshCw size={18} className="shrink-0 text-foreground" />
          <span className="text-[13.5px] text-foreground">
            Your knowledge base changed since this calendar was generated.
          </span>
          <button
            onClick={openGenerate}
            className="ml-auto text-[13px] font-semibold px-3 py-1.5 rounded-lg bg-accent text-accent-foreground transition active:scale-[0.97]"
          >
            Regenerate
          </button>
        </div>
      ) : null}

      {posts.length === 0 ? (
        <EmptyHint
          icon={<Calendar size={28} />}
          title="No calendar yet"
          body={
            !hasDocs
              ? "Add at least one document to your knowledge base, then generate."
              : connected.length === 0
                ? "Connect at least one social account, then generate."
                : "Generate a calendar of platform native posts from your knowledge base."
          }
          action={
            <div className="flex flex-col items-center gap-3">
              <PrimaryBtn onClick={openGenerate} disabled={!hasDocs || connected.length === 0}>
                Generate calendar
              </PrimaryBtn>
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[12.5px] text-faint">
                <Link to="/documents" className="flex items-center gap-1.5 hover:text-foreground transition">
                  {hasDocs ? <Check size={13} /> : <Circle size={13} />} {documents.length} document{documents.length !== 1 ? "s" : ""}
                </Link>
                <Link to="/accounts" className="flex items-center gap-1.5 hover:text-foreground transition">
                  {connected.length ? <Check size={13} /> : <Circle size={13} />} {connected.length} account{connected.length !== 1 ? "s" : ""}
                </Link>
                <Link to="/prompt" className="flex items-center gap-1.5 hover:text-foreground transition">
                  <Wand2 size={13} /> Edit prompt
                </Link>
              </div>
            </div>
          }
        />
      ) : (
        <>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <Segmented
              value={mode}
              onChange={setMode}
              options={[
                { value: "calendar", label: "Calendar", icon: <Calendar size={14} /> },
                { value: "queue", label: "Queue", icon: <List size={14} /> },
              ]}
            />
            {mode !== "calendar" ? (
              <div className="flex items-center gap-1.5 flex-wrap ml-auto">
                <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={`All · ${posts.length}`} />
                {connected.map((p) => {
                  const c = posts.filter((x) => x.platform === p).length;
                  if (!c) return null;
                  return (
                    <FilterChip
                      key={p}
                      active={filter === p}
                      onClick={() => setFilter(p)}
                      platform={p}
                      label={`${PLATFORMS[p].name} · ${c}`}
                    />
                  );
                })}
              </div>
            ) : null}
          </div>

          {mode === "calendar" ? (
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
              <CalendarGrid posts={posts} selected={selected} onSelect={setSelected} />
              <div className="max-w-[400px]">
                <div className="text-[13px] font-semibold text-foreground mb-3">
                  {selected ? fmtDateLong(selected) : "Select a day"}
                </div>
                {dayPosts.length ? (
                  <div className="space-y-5">
                    {dayPosts.map((p) => (
                      <PostCard key={p.id} post={p} {...cardHandlers(p)} />
                    ))}
                  </div>
                ) : (
                  <div className="border border-border rounded-2xl p-6 text-center text-[13.5px] text-faint bg-surface">
                    No posts scheduled for this day.
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {mode === "queue" ? (
            <div className="space-y-7">
              {byDate.map(({ date, items }) => (
                <div key={date}>
                  <div className="flex items-center gap-3 mb-3 sticky top-0 py-1.5 z-10 bg-background">
                    <div className="text-[13px] font-semibold text-foreground">{fmtDateLong(date)}</div>
                    <div className="text-[12px] text-faint">{items.length} post{items.length !== 1 ? "s" : ""}</div>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div className="grid lg:grid-cols-2 gap-5 items-start">
                    {items.map((p) => (
                      <div key={p.id} className="max-w-[440px] w-full mx-auto lg:mx-0">
                        <PostCard post={p} {...cardHandlers(p)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}

      <PostEditor
        open={!!editPost}
        post={editPost}
        onClose={() => setEditPost(null)}
        onSave={async (p) => {
          await updatePost(p);
          setEditPost(null);
        }}
      />
    </div>
  );
}